package com.example.positionahead.domain

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.Position
import com.example.positionahead.model.PositionLot
import com.example.positionahead.model.Quote
import com.example.positionahead.model.RebalanceTrade
import com.example.positionahead.model.totalShares
import kotlin.math.max
import kotlin.random.Random

object PlanSimulator {

    /**
     * Builds an in-memory portfolio after applying suggested dollar trades (for comparison only).
     */
    fun applyTrades(
        positions: List<Position>,
        trades: List<RebalanceTrade>,
        quotes: Map<String, Quote>,
    ): List<Position> {
        if (trades.isEmpty()) return positions

        val mutable = positions.map { it.deepCopyLocal() }.toMutableList()

        trades.forEach { trade ->
            trade.sellSymbol?.let { symbol ->
                applySell(mutable, symbol, trade.sellAmountDollars, quotes)
            }
            trade.buySymbol?.let { symbol ->
                applyBuy(mutable, symbol, trade.buyAmountDollars, quotes)
            }
        }

        return mutable.filter { it.totalShares() > 0.0001 }
    }

    fun summarizeAfterPlan(
        positions: List<Position>,
        trades: List<RebalanceTrade>,
        quotes: Map<String, Quote>,
    ): PortfolioSummary {
        val adjusted = applyTrades(positions, trades, quotes)
        return PortfolioCalculator.buildSummary(adjusted, quotes)
    }

    private fun applySell(
        positions: MutableList<Position>,
        symbol: String,
        sellDollars: Double,
        quotes: Map<String, Quote>,
    ) {
        if (sellDollars <= 0) return
        val index = positions.indexOfFirst { it.symbol.equals(symbol, ignoreCase = true) }
        if (index < 0) return

        val position = positions[index]
        val price = quotes[symbol.uppercase()]?.price?.takeIf { it > 0 } ?: return
        val sharesToSell = (sellDollars / price).coerceAtMost(position.totalShares())
        if (sharesToSell <= 0) return

        var remaining = sharesToSell
        val newLots = position.lots.mapNotNull { lot ->
            if (remaining <= 0) return@mapNotNull lot
            if (lot.shares <= remaining) {
                remaining -= lot.shares
                null
            } else {
                val updated = lot.copy(shares = lot.shares - remaining)
                remaining = 0.0
                updated
            }
        }
        positions[index] = position.copy(lots = newLots)
    }

    private fun applyBuy(
        positions: MutableList<Position>,
        symbol: String,
        buyDollars: Double,
        quotes: Map<String, Quote>,
    ) {
        if (buyDollars <= 0) return
        val price = quotes[symbol.uppercase()]?.price?.takeIf { it > 0 } ?: return
        val shares = buyDollars / price
        if (shares <= 0) return

        val index = positions.indexOfFirst { it.symbol.equals(symbol, ignoreCase = true) }
        val lot = PositionLot(
            id = Random.nextLong().toString(16),
            shares = shares,
            costBasisPerShare = price,
            purchaseDate = "Planned",
            note = "From Plan tab simulation",
        )
        if (index >= 0) {
            val position = positions[index]
            positions[index] = position.copy(lots = position.lots + lot)
        } else {
            positions += Position(
                id = Random.nextLong().toString(16),
                symbol = symbol.uppercase(),
                assetClass = guessAssetClass(symbol),
                lots = listOf(lot),
            )
        }
    }

    private fun guessAssetClass(symbol: String): AssetClass = when (symbol.uppercase()) {
        "BND", "AGG", "TLT", "BIV", "VCIT" -> AssetClass.BOND
        "VTI", "VOO", "QQQM", "SCHD", "SCHG", "VXUS", "JEPI", "JEPQ", "SMCY", "AMDY" -> AssetClass.ETF
        else -> AssetClass.STOCK
    }

    private fun Position.deepCopyLocal(): Position = copy(
        id = id,
        lots = lots.map { it.copy() },
    )
}
